<script>
    import { onMount, tick } from 'svelte';
    import { currentPage, currentRoutineStore, userStore } from '../store.js';
    import { navigate } from '../router.js';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Chip from '../components/Chip.svelte';
    import Banner from '../components/Banner.svelte';
    import EmptyState from '../components/EmptyState.svelte';
    import Field from '../components/Field.svelte';
    import Stat from '../components/Stat.svelte';
    import RoutineRating from '../components/RoutineRating.svelte';
    import { storedProfile, requestRecommendations } from '../recommend.js';

    let mode = 'search';
    let searchQuery = '';
    let searchResults = [];
    let searchTimeout;
    // Search results arrive out of order; only the newest query may paint.
    let searchSeq = 0;
    let isSearching = false;
    let pasteText = '';
    // Paste a product link: the page is read server-side for its name, brand,
    // ingredients and image, then matched against the catalogue.
    let linkUrl = '';
    let isReadingLink = false;
    let linkMatches = [];
    let linkMessage = '';
    // The catalogue is Sephora and Ulta. When it has nothing, the product may
    // still exist: search the web for it and read the page we land on.
    let isSearchingWeb = false;
    let webResults = [];
    let webMessage = '';
    let webSearchable = true;
    let searchedFor = '';

    // "Manyo glutathione serum" returns a Moon Juice vitamin C powder and a
    // Paula's Choice serum, both at 66%, because the catalogue has no Manyo at
    // all. Two wrong products read as "we looked and this is what there is",
    // so the way out has to be on the screen whether or not there are hits.
    $: queriedBrand = (searchQuery.trim().split(/\s+/)[0] || '').toLowerCase();
    $: brandMissing = queriedBrand.length > 2
        && searchResults.length > 0
        && !searchResults.some(r => `${r.brand || ''} ${r.name || ''}`.toLowerCase().includes(queriedBrand));
    let isParsing = false;
    let isSaving = false;
    let isAnalyzing = false;
    let screenshotPreview = '';
    let screenshotError = '';
    let routineItems = [];
    let analysis = null;
    // Set by the results screen when a new user comes here as the routine
    // step of onboarding, rather than from the nav.
    let onboarding = false;
    let isSkipping = false;
    let skipError = '';
    let importNotice = '';
    let resolver = {
        index: null,
        query: '',
        url: '',
        imagePreview: '',
        matches: [],
        isLoading: false,
        isResolvingUrl: false,
        isResolvingImage: false,
        message: ''
    };

    $: isLoggedIn = $userStore.isAuthenticated;
    $: morningItems = routineItems.filter(item => ['morning', 'both'].includes(item.timing));
    $: nightItems = routineItems.filter(item => ['night', 'both'].includes(item.timing));
    $: unmatchedItems = routineItems.filter(item => item.matchConfidence === 'unmatched' || !item.productId);

    // One rose primary on screen at a time. While the shelf is empty the import
    // panel owns it; once there are products the review panel does; once a
    // report exists the report does.
    $: hasItems = routineItems.length > 0;
    $: importIsPrimary = !hasItems;
    $: analyzeIsPrimary = hasItems && !analysis;

    onMount(async () => {
        onboarding = localStorage.getItem('onboardingStep') === 'routine';
        await loadRoutine();
    });

    // "I don't have a routine": straight to a full routine.
    async function skipToFullRoutine() {
        isSkipping = true;
        skipError = '';
        localStorage.removeItem('onboardingStep');
        const result = await requestRecommendations();
        if (!result.ok) skipError = result.error;
        isSkipping = false;
    }

    function goToNextStep() {
        localStorage.removeItem('onboardingStep');
        navigate('next-step');
    }

    async function loadRoutine() {
        const saved = localStorage.getItem('currentRoutine');
        if (saved) {
            try {
                routineItems = JSON.parse(saved) || [];
                currentRoutineStore.set(routineItems);
            } catch (e) {}
        }

        if (!isLoggedIn) return;

        try {
            const response = await fetch('/api/current-routine', { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                if (data.items && data.items.length > 0) {
                    routineItems = data.items.map(fromApiItem);
                    persistLocal();
                }
            }
        } catch (e) {
            console.error('Routine load error:', e);
        }
    }

    function fromApiItem(item) {
        return {
            productId: item.productId,
            productName: item.productName,
            brand: item.brand || '',
            productType: item.productType || '',
            imageUrl: item.imageUrl || '',
            productUrl: item.productUrl || '',
            timing: item.timing || 'both',
            frequency: item.frequency || 'daily',
            status: item.status || 'keep',
            source: item.source || 'manual',
            notes: item.notes || '',
            prescribed: !!item.prescribed,
            ingredientSource: item.ingredientSource || '',
            ingredientSourceUrl: item.ingredientSourceUrl || '',
            startedAt: item.startedAt || null,
            matchConfidence: item.matchConfidence || (item.source && item.source.startsWith('external') ? 'external' : item.productId ? 'catalog' : 'unmatched')
        };
    }

    // The codemod folded the old #C98872 "needs attention" warm red into
    // --rose, the brand accent, which made a verdict look like decoration.
    // The state tokens carry the meaning again: a confirmed catalog match is
    // success, anything still unconfirmed is caution.
    function matchTone(item) {
        return item.matchConfidence === 'catalog' ? 'success' : 'caution';
    }

    function matchLabel(item) {
        if (item.matchConfidence === 'catalog') return 'Catalog match';
        if (item.matchConfidence === 'external') return 'External match';
        return 'Needs match';
    }

    function debounceSearch() {
        clearTimeout(searchTimeout);
        // A new query invalidates the last web search, but a web search the
        // person asked for afterwards must not be wiped by a catalogue result
        // still in flight.
        webResults = [];
        webMessage = '';
        // A link in the search box returns nothing, which reads as "we don't
        // have it". Send it where it can actually be read.
        if (looksLikeUrl(searchQuery)) {
            linkUrl = searchQuery.trim();
            searchQuery = '';
            searchResults = [];
            mode = 'link';
            readProductLink();
            return;
        }
        if (searchQuery.length < 2) {
            searchResults = [];
            return;
        }
        searchTimeout = setTimeout(searchProducts, 250);
    }

    async function searchProducts() {
        const seq = ++searchSeq;
        isSearching = true;
        searchedFor = searchQuery.trim();
        try {
            const response = await fetch(`/api/product-search?q=${encodeURIComponent(searchQuery)}`);
            const results = response.ok ? await response.json() : [];
            if (seq !== searchSeq) return;       // a newer query is already on screen
            searchResults = results;
        } catch (e) {
            console.error('Routine search error:', e);
        } finally {
            if (seq === searchSeq) isSearching = false;
        }
    }

    // 'added' | 'refreshed' | 'duplicate'. The caller needs to know: saying
    // "Added" over a product that was refused is how someone ends up pressing
    // the same row again and again while the old, wrong row sits underneath.
    function addProduct(product, source = 'search', extra = {}) {
        const incomingId = product.id || product.productId || '';
        const incomingName = normalizeName(product.name || product.productName || '');
        const existingIndex = routineItems.findIndex(item => (
            incomingId && item.productId === incomingId
        ) || (
            incomingName && normalizeName(item.productName) === incomingName
        ));

        if (existingIndex !== -1) {
            // A product already on the shelf, read again from its page. The
            // reason to paste the same link twice is that what we stored was
            // wrong, so take the better reading rather than refusing it: a
            // real name over a page title, a photo, and above all an
            // ingredient list where we had none.
            const existing = routineItems[existingIndex];
            const better = {};
            if (product.name && product.name !== existing.productName) better.productName = product.name;
            if (product.brand && product.brand !== existing.brand) better.brand = product.brand;
            if (product.imageUrl && product.imageUrl !== existing.imageUrl) better.imageUrl = product.imageUrl;
            if (product.productType && !existing.productType) better.productType = product.productType;

            // Ingredients only ever improve: a page that carries a list
            // replaces whatever was there, and a page that carries none
            // clears text we now know was never a formula.
            const incomingNotes = product.ingredients ? `Ingredients: ${product.ingredients}` : '';
            if (product.ingredientStatus && incomingNotes !== existing.notes) better.notes = incomingNotes;

            if (Object.keys(better).length === 0) {
                importNotice = 'That product is already in your routine, and nothing we read about it changed.';
                return 'duplicate';
            }

            routineItems = routineItems.map((item, i) => (
                i === existingIndex ? { ...item, ...better, source } : item
            ));
            importNotice = '';
            persistLocal();
            if (!product.ingredients && source !== 'catalog') {
                findIngredients(existingIndex);
            }
            return 'refreshed';
        }

        routineItems = [
            ...routineItems,
            {
                productId: incomingId,
                productName: product.name || product.productName || '',
                brand: product.brand || '',
                productType: product.productType || '',
                imageUrl: product.imageUrl || '',
                productUrl: product.url || product.productUrl || '',
                timing: 'both',
                frequency: 'daily',
                status: 'keep',
                source,
                // Ingredients read from a product page travel with the item:
                // the rater reads them, so a product we do not stock still
                // gets scored on what is in it.
                notes: product.ingredients ? `Ingredients: ${product.ingredients}` : '',
                matchConfidence: extra.matchConfidence
                    || (product.id || product.productId ? 'catalog' : 'unmatched'),
                ...extra
            }
        ];
        searchQuery = '';
        searchResults = [];
        analysis = null;
        importNotice = '';
        persistLocal();
        // A product with no formula cannot be scored, so start looking for one
        // straight away rather than waiting to be asked. What comes back is
        // offered, never applied.
        if (!product.ingredients && source !== 'catalog') {
            findIngredients(routineItems.length - 1);
        }
        return 'added';
    }

    // Whether a match is a product the shelf already holds, so the row can
    // say what pressing it does.
    function alreadyOnShelf(match) {
        const id = match.id || match.productId || '';
        const name = normalizeName(match.name || match.productName || '');
        return routineItems.some(item => (
            id && item.productId === id
        ) || (
            name && normalizeName(item.productName) === name
        ));
    }

    function normalizeName(value) {
        return String(value || '').toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    }

    function mergeRoutineItems(existingItems, incomingItems) {
        const merged = [...existingItems];
        incomingItems.forEach(item => {
            const incomingId = item.productId || item.id || '';
            const incomingName = normalizeName(item.productName || item.name);
            const exists = merged.some(existing => (
                incomingId && existing.productId === incomingId
            ) || (
                incomingName && normalizeName(existing.productName) === incomingName
            ));
            if (!exists) merged.push(item);
        });
        return merged;
    }

    function addCustomProduct() {
        if (!searchQuery.trim()) return;
        addProduct({ productName: searchQuery.trim() }, 'manual');
    }

    async function parseRoutine() {
        if (!pasteText.trim()) return;
        isParsing = true;
        try {
            const response = await fetch('/api/routine-parse', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: pasteText })
            });
            if (response.ok) {
                const data = await response.json();
                const beforeCount = routineItems.length;
                routineItems = mergeRoutineItems(routineItems, data.items || []);
                const addedCount = routineItems.length - beforeCount;
                importNotice = addedCount === 0 ? 'No new products found. They may already be in your routine.' : `Added ${addedCount} product${addedCount === 1 ? '' : 's'} from your paste.`;
                pasteText = '';
                analysis = null;
                persistLocal();
            }
        } catch (e) {
            console.error('Routine parse error:', e);
        }
        isParsing = false;
    }

    async function handleScreenshotUpload(event) {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        screenshotPreview = URL.createObjectURL(file);
        screenshotError = '';
        isParsing = true;

        const formData = new FormData();
        formData.append('screenshot', file);

        try {
            const response = await fetch('/api/product-detect-from-image', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            if (response.ok && data.matches && data.matches.length > 0) {
                const beforeCount = routineItems.length;
                routineItems = mergeRoutineItems(routineItems, data.matches.map(match => ({
                        productId: match.id || '',
                        productName: match.name || '',
                        brand: match.brand || '',
                        productType: match.productType || '',
                        imageUrl: match.imageUrl || '',
                        productUrl: match.url || '',
                        timing: 'both',
                        frequency: 'daily',
                        status: 'keep',
                        source: 'screenshot',
                        notes: '',
                        matchConfidence: 'catalog'
                    })));
                const addedCount = routineItems.length - beforeCount;
                importNotice = addedCount === 0 ? 'No new products found. They may already be in your routine.' : `Added ${addedCount} product${addedCount === 1 ? '' : 's'} from your image.`;
                persistLocal();
            } else {
                screenshotError = data.error || 'SkinGenie could not match products from that image yet.';
            }
        } catch (e) {
            console.error('Routine screenshot error:', e);
            screenshotError = 'Unable to process this image right now.';
        }

        isParsing = false;
    }

    // Ingredients live inside the item's notes, which is what the rater reads
    // and what gets queued for the catalogue.
    function ingredientsOf(item) {
        const match = /\bIngredients?\s*:\s*([\s\S]+)$/i.exec(item.notes || '');
        return match ? match[1].trim() : '';
    }

    function noteOf(item) {
        return (item.notes || '').replace(/\bIngredients?\s*:[\s\S]*$/i, '').trim();
    }

    function setNote(index, value) {
        const item = routineItems[index];
        const ingredients = ingredientsOf(item);
        const notes = [(value || '').trim(), ingredients ? `Ingredients: ${ingredients}` : '']
            .filter(Boolean).join('\n');
        updateItem(index, 'notes', notes);
    }

    function setIngredients(index, value) {
        const text = (value || '').trim();
        const item = routineItems[index];
        const rest = (item.notes || '').replace(/\bIngredients?\s*:[\s\S]*$/i, '').trim();
        const notes = [rest, text ? `Ingredients: ${text}` : ''].filter(Boolean).join('\n');
        // Typed by hand, so it is no longer whatever site we borrowed it from.
        routineItems = routineItems.map((existing, i) => (
            i === index ? { ...existing, notes, ingredientSource: '', ingredientSourceUrl: '' } : existing
        ));
        analysis = null;
        persistLocal();
        if (text) importNotice = 'Thanks. SkinGenie will score that product on its ingredients.';
    }

    // Looking for an ingredient list somewhere other than the product's own
    // page, keyed by product so it survives a re-order of the shelf.
    let ingredientHunts = {};

    // How far the list is from the people who made the product. Each step of
    // transcription is a chance for an error or a stale formula.
    function sourceLabel(kind) {
        if (kind === 'brand') return "the brand's own site";
        if (kind === 'retailer') return 'a shop that sells it';
        if (kind === 'database') return 'an ingredient database';
        return 'another site';
    }

    function huntKey(item) {
        return item.productId || item.productName || '';
    }

    function setHunt(key, value) {
        ingredientHunts = value === null
            ? Object.fromEntries(Object.entries(ingredientHunts).filter(([k]) => k !== key))
            : { ...ingredientHunts, [key]: value };
    }

    async function findIngredients(index) {
        const item = routineItems[index];
        if (!item) return;
        const key = huntKey(item);
        setHunt(key, { loading: true, candidates: [], message: '' });

        try {
            const response = await fetch('/api/product-ingredients', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: item.productName,
                    brand: item.brand,
                    productType: item.productType
                })
            });
            const data = await response.json();
            setHunt(key, {
                loading: false,
                candidates: data.candidates || [],
                configured: data.configured !== false,
                message: data.message || ''
            });
        } catch (error) {
            console.error('Ingredient lookup error:', error);
            setHunt(key, { loading: false, candidates: [], message: 'Could not look that up right now.' });
        }
    }

    // Nothing is attached without the person saying so. A borrowed formula
    // drives the safety warnings and the score, so it is theirs to accept.
    function useFoundIngredients(index, candidate) {
        const item = routineItems[index];
        const rest = (item.notes || '').replace(/\bIngredients?\s*:[\s\S]*$/i, '').trim();
        const notes = [rest, `Ingredients: ${candidate.ingredients}`].filter(Boolean).join('\n');
        routineItems = routineItems.map((existing, i) => (
            i === index
                ? { ...existing, notes, ingredientSource: candidate.source, ingredientSourceUrl: candidate.sourceUrl }
                : existing
        ));
        analysis = null;
        persistLocal();
        setHunt(huntKey(item), null);
        importNotice = `Ingredients taken from ${candidate.source}. SkinGenie will score that product on them.`;
    }

    function updateItem(index, field, value) {
        routineItems = routineItems.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item);
        analysis = null;
        persistLocal();
    }

    function removeItem(index) {
        routineItems = routineItems.filter((_, itemIndex) => itemIndex !== index);
        analysis = null;
        if (resolver.index === index) closeResolver();
        persistLocal();
    }

    function openResolver(index) {
        const item = routineItems[index];
        resolver = {
            index,
            query: [item.brand, item.productName].filter(Boolean).join(' ').trim(),
            url: '',
            imagePreview: '',
            matches: [],
            isLoading: false,
            isResolvingUrl: false,
            isResolvingImage: false,
            message: ''
        };
        resolveProduct();
    }

    function closeResolver() {
        resolver = {
            index: null,
            query: '',
            url: '',
            imagePreview: '',
            matches: [],
            isLoading: false,
            isResolvingUrl: false,
            isResolvingImage: false,
            message: ''
        };
    }

    async function resolveProduct() {
        if (resolver.index === null || !resolver.query.trim()) return;

        resolver = { ...resolver, isLoading: true, message: '', matches: [] };
        try {
            const response = await fetch('/api/product-resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: resolver.query })
            });
            const data = await response.json();
            resolver = {
                ...resolver,
                matches: data.matches || [],
                message: data.message || (response.ok ? '' : 'Unable to search right now.'),
                isLoading: false
            };
        } catch (e) {
            console.error('Product resolve error:', e);
            resolver = { ...resolver, matches: [], message: 'Unable to search right now.', isLoading: false };
        }
    }

    function looksLikeUrl(value) {
        return /^https?:\/\/\S+\.\S+/i.test((value || '').trim());
    }

    async function readProductLink() {
        const url = linkUrl.trim();
        if (!looksLikeUrl(url)) {
            linkMessage = 'Paste the whole link, starting with https://';
            return;
        }

        isReadingLink = true;
        linkMessage = '';
        linkMatches = [];
        try {
            const response = await fetch('/api/product-resolve-url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url })
            });
            const data = await response.json();
            linkMatches = data.matches || [];
            linkMessage = data.message || (response.ok ? '' : 'Unable to read that link.');
        } catch (e) {
            console.error('Product link read error:', e);
            linkMessage = 'Unable to read that link right now.';
        }
        isReadingLink = false;
    }

    async function searchTheWeb() {
        const query = searchQuery.trim() || searchedFor;
        if (query.length < 3) return;

        isSearchingWeb = true;
        webMessage = '';
        webResults = [];
        try {
            const response = await fetch('/api/product-web-search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query })
            });
            const data = await response.json();
            webResults = data.results || [];
            webMessage = data.message || '';
            webSearchable = data.configured !== false;
        } catch (e) {
            console.error('Web product search error:', e);
            webMessage = 'Could not search the web just now. Paste the product link instead.';
        }
        isSearchingWeb = false;
    }

    function addFromWeb(match) {
        addFromLink(match);
        webResults = [];
        searchQuery = '';
    }

    function addFromLink(match) {
        const isCatalogMatch = !!match.id && !String(match.id).startsWith('external:');
        const outcome = addProduct(match, isCatalogMatch ? 'catalog' : 'external_url', {
            matchConfidence: isCatalogMatch ? 'catalog' : 'external'
        });
        if (outcome === 'duplicate') {
            // Leave the link and the match on screen. Clearing them here is
            // what made this look stuck: the row vanished, the message said
            // it was added, and the shelf below never changed.
            return;
        }
        linkUrl = '';
        linkMatches = [];
        if (outcome === 'refreshed') {
            linkMessage = 'Updated that product with what we just read from its page.';
        } else if (isCatalogMatch) {
            linkMessage = 'Added from your catalog.';
        } else {
            linkMessage = match.ingredientStatus === 'found'
                ? 'Added. SkinGenie will use the ingredients it read from that page.'
                : 'Added. That page had no ingredient list, so we are looking for one elsewhere.';
        }
    }

    async function resolveProductUrl() {
        if (resolver.index === null || !resolver.url.trim()) return;

        resolver = { ...resolver, isResolvingUrl: true, message: '' };
        try {
            const response = await fetch('/api/product-resolve-url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: resolver.url })
            });
            const data = await response.json();
            resolver = {
                ...resolver,
                matches: data.matches || [],
                message: data.message || (response.ok ? '' : 'Unable to read that link.'),
                isResolvingUrl: false
            };
        } catch (e) {
            console.error('Product URL resolve error:', e);
            resolver = { ...resolver, message: 'Unable to read that link.', isResolvingUrl: false };
        }
    }

    async function resolveProductImage(event) {
        const file = event.target.files && event.target.files[0];
        if (resolver.index === null || !file) return;

        resolver = {
            ...resolver,
            imagePreview: URL.createObjectURL(file),
            isResolvingImage: true,
            message: ''
        };

        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await fetch('/api/product-detect-from-image', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            resolver = {
                ...resolver,
                matches: data.matches || [],
                message: data.matches && data.matches.length > 0 ? 'Confirm the matching product.' : data.error || 'No catalog match found from that image yet.',
                isResolvingImage: false
            };
        } catch (e) {
            console.error('Product image resolve error:', e);
            resolver = { ...resolver, message: 'Unable to read that image.', isResolvingImage: false };
        }
    }

    function confirmResolvedProduct(match) {
        if (resolver.index === null) return;
        const isCatalogMatch = match.source === 'catalog';

        routineItems = routineItems.map((item, itemIndex) => itemIndex === resolver.index ? {
            ...item,
            productId: match.id || '',
            productName: match.name || item.productName,
            brand: match.brand || item.brand,
            productType: match.productType || item.productType,
            imageUrl: match.imageUrl || item.imageUrl,
            productUrl: match.url || item.productUrl,
            source: match.source || 'catalog',
            matchConfidence: isCatalogMatch ? 'catalog' : 'external',
            notes: match.ingredients ? [item.notes, `Ingredients: ${match.ingredients}`].filter(Boolean).join('\n') : item.notes
        } : item);
        importNotice = isCatalogMatch ? 'Matched product confirmed.' : 'External product confirmed. SkinGenie will use any extracted ingredients and keep it marked for catalog enrichment.';
        analysis = null;
        closeResolver();
        persistLocal();
    }

    function persistLocal() {
        localStorage.setItem('currentRoutine', JSON.stringify(routineItems));
        currentRoutineStore.set(routineItems);
    }

    async function saveRoutine() {
        persistLocal();
        if (!isLoggedIn) return;

        isSaving = true;
        try {
            await fetch('/api/current-routine', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ items: routineItems })
            });
        } catch (e) {
            console.error('Routine save error:', e);
        }
        isSaving = false;
    }

    async function analyzeRoutine() {
        await saveRoutine();
        isAnalyzing = true;
        try {
            // The fresh analysis travels with the request, so a new user is
            // rated against the photo they just took. A returning user with
            // nothing in this browser is rated against their stored profile.
            const profile = storedProfile();
            const response = await fetch('/api/routine-analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    items: routineItems,
                    userId: profile.userId || $userStore.userId,
                    profile: profile.areasOfImprovement ? profile : null
                })
            });
            if (response.ok) {
                analysis = await response.json();
                await tick();
                document.querySelector('.report')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                localStorage.setItem('routineRating', JSON.stringify({
                    score: analysis.score,
                    verdict: analysis.verdict || analysis.nextAction || '',
                    fixes: (analysis.fixes || []).map(f => f.title),
                    ratedAt: new Date().toISOString()
                }));
            }
        } catch (e) {
            console.error('Routine analysis error:', e);
        }
        isAnalyzing = false;
    }

    function goToScanner() {
        navigate('scanner');
    }

    function goToProgress() {
        navigate('progress');
    }
</script>

<style>
    /* --- Page ------------------------------------------------------------
       Was max-width 1120px + a hand-rolled 120px top margin + 40px gutters.
       All three are --content-max / --page-top / --page-pad-x, so .u-page
       replaces the lot and the nav gap finally matches the other screens. */

    .hero {
        max-width: var(--prose-max);
        margin-bottom: var(--space-12);
    }

    .eyebrow {
        color: var(--sage);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        margin-bottom: var(--space-4);
    }

    h1, h2 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        margin: 0;
    }

    h1 {
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
    }

    h2 {
        font-size: var(--text-xl);
        line-height: var(--leading-snug);
    }

    .lead {
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
        margin: var(--space-5) 0 0;
    }

    .muted {
        color: var(--text-muted);
        font-size: var(--text-sm);
        line-height: var(--leading-normal);
        margin: var(--space-3) 0 0;
    }

    /* Intrinsic two-up. Replaces the 0.95fr/1.05fr grid plus its 820px
       breakpoint: it folds to one column whenever a column would go under
       340px, which happens well before any phone. */
    .shell {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
        gap: var(--space-6);
        align-items: start;
    }

    /* --- Import panel ----------------------------------------------------- */

    .tabs {
        display: flex;
        gap: var(--space-6);
        border-bottom: 1px solid var(--border);
        margin-bottom: var(--space-6);
    }

    .tabs button {
        appearance: none;
        background: none;
        border: 0;
        border-bottom: 2px solid transparent;
        border-radius: var(--radius-0);
        margin: 0 0 -1px;
        padding: 0 0 var(--space-3);
        font-family: var(--font-body);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
        cursor: pointer;
        transition: color var(--transition), border-color var(--transition);
    }

    .tabs button:hover { color: var(--cream); }

    .tabs button.active {
        color: var(--sage);
        border-bottom-color: var(--sage);
    }

    input,
    textarea,
    select {
        width: 100%;
        background: var(--bg);
        border: 1px solid var(--border);
        border-radius: var(--radius-0);
        color: var(--cream);
        padding: var(--space-3);
        font-family: var(--font-body);
        font-size: var(--text-sm);
    }

    textarea {
        min-height: 180px;
        resize: vertical;
        line-height: var(--leading-normal);
    }

    .results {
        border-top: 1px solid var(--border);
        margin: var(--space-5) 0;
        max-height: 340px;
        overflow: auto;
    }

    .result {
        width: 100%;
        display: flex;
        align-items: center;
        gap: var(--space-3);
        background: transparent;
        border: 0;
        border-bottom: 1px solid var(--border);
        border-radius: var(--radius-0);
        color: var(--cream);
        margin: 0;
        padding: var(--space-3) var(--space-2);
        text-align: left;
        font-family: var(--font-body);
        font-size: var(--text-sm);
        cursor: pointer;
        transition: background var(--transition);
    }

    .result:hover { background: var(--hover-veil); }

    .provenance, .hunting {
        color: var(--text-muted);
        font-size: var(--text-2xs);
        margin: var(--space-2) 0 var(--space-3);
        line-height: var(--leading-relaxed);
    }

    .provenance a, .find-source a { color: var(--sage); }

    .finds { display: grid; gap: var(--space-3); margin-bottom: var(--space-4); }

    .find {
        border: 1px solid var(--border);
        padding: var(--space-3);
        background: var(--surface);
    }

    .find-source {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        margin: 0 0 var(--space-2);
    }

    .find-kind { color: var(--text-muted); }

    .find-list {
        color: var(--text-muted);
        font-size: var(--text-2xs);
        line-height: var(--leading-relaxed);
        margin: 0 0 var(--space-3);
        max-height: 5.4em;
        overflow: auto;
    }

    /* The row IS the button, so it has to look like one. Without this it
       reads as a result card and people wait for an Add button that is not
       coming. */
    .result-action {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: var(--space-3);
        flex: none;
    }

    .action-word {
        border: 1px solid var(--border-strong);
        padding: var(--space-1) var(--space-3);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--cream);
        white-space: nowrap;
    }

    .result:hover .action-word {
        border-color: var(--sage);
        color: var(--sage);
    }

    .result-name { display: block; }

    .thumb {
        width: 44px;
        height: 44px;
        flex: none;
        object-fit: cover;
        background: var(--cream);
    }

    .mark {
        display: block;
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
        margin-top: var(--space-1);
    }

    .upload-target {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-height: 160px;
        padding: var(--space-6);
        border: 1px dashed var(--border-strong);
        color: var(--text-muted);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        cursor: pointer;
        transition: border-color var(--transition), color var(--transition);
    }

    .upload-inline {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-3) var(--space-5);
        border: 1px dashed var(--border-strong);
        color: var(--text-muted);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        cursor: pointer;
        transition: border-color var(--transition), color var(--transition);
    }

    .upload-target:hover,
    .upload-inline:hover {
        border-color: var(--cream);
        color: var(--cream);
    }

    .upload-target input,
    .upload-inline input { display: none; }

    .preview {
        max-width: 180px;
        max-height: 180px;
        object-fit: contain;
        display: block;
        margin: var(--space-5) auto 0;
    }

    .notice { margin-top: var(--space-5); }

    /* --- Review panel ----------------------------------------------------- */

    .summary {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(84px, 1fr));
        gap: var(--space-5);
        padding-bottom: var(--space-6);
        border-bottom: 1px solid var(--border);
        margin-bottom: var(--space-6);
    }

    /* A calm inventory, not a stack of boxes: one hairline per product over
       the panel's own ground, no card, no fill. */
    .routine-list {
        border-top: 1px solid var(--border);
        margin-top: var(--space-6);
    }

    .routine-row {
        border-bottom: 1px solid var(--border);
        padding: var(--space-5) 0;
    }

    .row-head {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        margin-bottom: var(--space-5);
    }

    .row-id {
        flex: 1 1 180px;
        min-width: 0;
    }

    .product-name {
        margin: 0;
        font-size: var(--text-base);
        line-height: var(--leading-snug);
        color: var(--cream);
    }

    .row-controls {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        column-gap: var(--space-4);
        row-gap: 0;
    }

    /* Quiet until you reach for it: the remove control is a ghost button, so
       it reads as muted text at rest and only lifts to cream on hover. */
    .row-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
        align-items: center;
    }

    .footer-actions {
        display: flex;
        gap: var(--space-3);
        flex-wrap: wrap;
        margin-top: var(--space-6);
    }

    .caution-note { margin-top: var(--space-6); }

    /* --- Resolver --------------------------------------------------------- */

    .resolver {
        border: 1px solid var(--sage-line);
        background: var(--sage-veil);
        padding: var(--space-5);
        margin-top: var(--space-5);
    }

    .resolver-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-bottom: var(--space-5);
    }

    .resolver-results {
        display: grid;
        gap: var(--space-2);
        margin-top: var(--space-4);
    }

    .resolver-result {
        width: 100%;
        display: flex;
        align-items: center;
        gap: var(--space-3);
        background: var(--bg);
        border: 1px solid var(--border);
        border-radius: var(--radius-0);
        color: var(--cream);
        margin: 0;
        padding: var(--space-3);
        text-align: left;
        font-family: var(--font-body);
        font-size: var(--text-sm);
        cursor: pointer;
        transition: border-color var(--transition);
    }

    .resolver-result:hover { border-color: var(--sage-line); }

    .confidence {
        margin-left: auto;
        flex: none;
        color: var(--sage);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        white-space: nowrap;
    }

    .resolver-image-preview {
        width: 96px;
        height: 96px;
        object-fit: cover;
        display: block;
        margin-top: var(--space-4);
        background: var(--bg);
        border: 1px solid var(--border);
    }

    /* --- Report ----------------------------------------------------------- */

    .skip { margin-top: var(--space-4); }

    .nothing {
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border);
    }

    .nothing .muted { margin-bottom: var(--space-4); }

    .from-web {
        margin: var(--space-6) 0 var(--space-2);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--sage);
    }

    .prescribed span { white-space: nowrap; }
    .prescribed input { width: auto; margin: 0; padding: 0; }

    .prescribed {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        margin: var(--space-3) 0;
        color: var(--text-muted);
        font-size: var(--text-sm);
        cursor: pointer;
    }

    .report {
        margin-top: var(--section-gap);
        padding-top: var(--space-10);
        border-top: 1px solid var(--border);
    }


</style>

<div class="u-page">
    <header class="hero">
        {#if onboarding}
            <div class="eyebrow">Step 3 of 4 · Your current routine</div>
            <h1>What do you use now?</h1>
            <p class="lead">Add the products on your shelf. SkinGenie rates the routine for the skin in your photo, then you choose what comes next.</p>
            <div class="skip">
                <Button variant="ghost" size="sm" loading={isSkipping} busyLabel="Building your routine" on:click={skipToFullRoutine}>I don’t have a routine yet</Button>
                {#if skipError}<p class="muted">{skipError}</p>{/if}
            </div>
        {:else}
            <div class="eyebrow">Your current routine</div>
            <h1>Bring your shelf into SkinGenie.</h1>
            <p class="lead">Add what you already use. SkinGenie rates it for your skin and suggests the smallest useful change instead of replacing everything.</p>
        {/if}
    </header>

    <div class="shell">
        <Card>
            <div class="tabs">
                <button class:active={mode === 'search'} type="button" on:click={() => mode = 'search'}>Search</button>
                <button class:active={mode === 'paste'} type="button" on:click={() => mode = 'paste'}>Paste</button>
                <button class:active={mode === 'link'} type="button" on:click={() => mode = 'link'}>Link</button>
                <button class:active={mode === 'photo'} type="button" on:click={() => mode = 'photo'}>Photo</button>
            </div>

            {#if mode === 'search'}
                <Field label="Find a product" hint="Type at least two letters to search the catalog." let:id let:describedBy>
                    <input {id} aria-describedby={describedBy} bind:value={searchQuery} on:input={debounceSearch} placeholder="Search a product you already use" />
                </Field>

                {#if isSearching}
                    <p class="muted">Searching your catalog…</p>
                {/if}

                {#if searchResults.length > 0}
                    <div class="results">
                        {#each searchResults as result}
                            <button class="result" type="button" on:click={() => addProduct(result)}>
                                {#if result.imageUrl}
                                    <img class="thumb" src={result.imageUrl} alt={result.name} />
                                {/if}
                                <span class="result-name">
                                    {result.name}
                                    <span class="mark">{result.brand} · {result.productType}</span>
                                </span>
                            </button>
                        {/each}
                    </div>
                {/if}

                {#if searchQuery.length > 2 && !isSearching}
                    <div class="nothing">
                        <p class="muted">
                            {#if searchResults.length === 0}
                                Not in our catalog. We stock what Sephora and Ulta carry, so plenty
                                of good products are missing.
                            {:else if brandMissing}
                                Not seeing it? We have nothing from
                                <strong>{searchQuery.trim().split(/\s+/)[0]}</strong> in the catalog,
                                so those are the closest names rather than your product.
                            {:else}
                                Not the one? We stock what Sephora and Ulta carry, so plenty of good
                                products are missing.
                            {/if}
                        </p>
                        <div class="row-actions">
                            {#if webSearchable}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    loading={isSearchingWeb}
                                    busyLabel="Looking"
                                    on:click={searchTheWeb}
                                >Search the web for it</Button>
                            {/if}
                            <Button variant="secondary" size="sm" on:click={() => mode = 'link'}>Paste its link</Button>
                            <Button variant="ghost" size="sm" on:click={addCustomProduct}>Add it anyway</Button>
                        </div>
                    </div>
                {/if}

                {#if webMessage}
                    <div class="notice">
                        <Banner tone="info">{webMessage}</Banner>
                    </div>
                {/if}

                {#if webResults.length > 0}
                    <p class="from-web">From the web</p>
                    <div class="results web">
                        {#each webResults as match}
                            <button class="result" type="button" on:click={() => addFromWeb(match)}>
                                {#if match.imageUrl}
                                    <img class="thumb" src={match.imageUrl} alt={match.name} />
                                {/if}
                                <span class="result-name">
                                    {match.name}
                                    <span class="mark">
                                        {match.brand || 'Unknown brand'} · {match.productType || 'product'}
                                        {#if String(match.id || '').startsWith('external:')}
                                            · {match.ingredientStatus === 'found' ? 'ingredients read' : 'no ingredients on that page'}
                                        {:else}
                                            · in our catalog
                                        {/if}
                                    </span>
                                </span>
                            </button>
                        {/each}
                    </div>
                {/if}
            {:else if mode === 'paste'}
                <Field label="Paste your routine" hint="One product per line, or an AM and PM list." let:id let:describedBy>
                    <textarea {id} aria-describedby={describedBy} bind:value={pasteText} placeholder={"AM: cleanser, vitamin C, moisturizer, SPF\nPM: oil cleanser, tretinoin, moisturizer"}></textarea>
                </Field>
                <Button
                    variant={importIsPrimary ? 'primary' : 'secondary'}
                    loading={isParsing}
                    busyLabel="Parsing"
                    on:click={parseRoutine}
                >Parse routine</Button>
            {:else if mode === 'link'}
                <Field
                    label="Product link"
                    hint="From Sephora, Ulta, Amazon, a brand's own site, anywhere."
                    let:id
                    let:describedBy
                >
                    <input
                        {id}
                        type="url"
                        aria-describedby={describedBy}
                        bind:value={linkUrl}
                        on:keydown={(e) => e.key === 'Enter' && readProductLink()}
                        placeholder="https://www.sephora.com/product/..."
                    />
                </Field>
                <Button
                    variant={importIsPrimary ? 'primary' : 'secondary'}
                    loading={isReadingLink}
                    busyLabel="Reading the page"
                    on:click={readProductLink}
                >Read this link</Button>

                {#if linkMessage}
                    <div class="notice">
                        <Banner tone="info">{linkMessage}</Banner>
                    </div>
                {/if}

                {#if linkMatches.length > 0}
                    <p class="muted">Pick the right one. Catalog products come with full ingredient data.</p>
                    <div class="results">
                        {#each linkMatches as match}
                            <button class="result" type="button" on:click={() => addFromLink(match)}>
                                {#if match.imageUrl}
                                    <img class="thumb" src={match.imageUrl} alt={match.name} />
                                {/if}
                                <span class="result-name">
                                    {match.name}
                                    <span class="mark">
                                        {match.brand || 'Unknown brand'} · {match.productType || 'product'}
                                        {#if String(match.id || '').startsWith('external:')}
                                            · {match.ingredientStatus === 'found' ? 'ingredients read' : 'no ingredients on that page'}
                                        {:else}
                                            · in our catalog
                                        {/if}
                                    </span>
                                </span>
                                <span class="result-action">
                                    {#if match.confidence}<span class="confidence">{match.confidence}%</span>{/if}
                                    <span class="action-word">{alreadyOnShelf(match) ? 'Re-read' : 'Add'}</span>
                                </span>
                            </button>
                        {/each}
                    </div>
                {/if}
            {:else}
                <Field label="Shelf photo or screenshot" hint="This uses the same product detection as the scanner. You can confirm and edit everything before saving." let:id>
                    <label class="upload-target" for={id}>
                        <input {id} type="file" accept="image/png,image/jpeg,image/webp" on:change={handleScreenshotUpload} />
                        Upload shelf photo or routine screenshot
                    </label>
                </Field>

                {#if screenshotPreview}
                    <img class="preview" src={screenshotPreview} alt="Routine import preview" />
                {/if}
                {#if screenshotError}
                    <div class="notice">
                        <Banner tone="danger">{screenshotError}</Banner>
                    </div>
                {/if}
            {/if}

            {#if importNotice}
                <div class="notice">
                    <Banner tone="info">{importNotice}</Banner>
                </div>
            {/if}
        </Card>

        {#if !hasItems}
            <EmptyState
                title="Your shelf is empty here."
                body="Add the products you already use. SkinGenie reads their ingredients, scores the routine as a whole, then suggests the smallest useful change instead of a new shelf."
            />
        {:else}
            <Card>
                <div class="summary">
                    <Stat align="start" value={routineItems.length} label="Products" />
                    <Stat align="start" value={morningItems.length} label="Morning" />
                    <Stat align="start" value={nightItems.length} label="Night" />
                </div>

                <h2>Review your routine</h2>
                <p class="muted">Confirm timing and frequency. Unmatched products still count, but catalog matches give SkinGenie stronger ingredient data.</p>

                <div class="routine-list">
                    {#each routineItems as item, index}
                        <div class="routine-row">
                            <div class="row-head">
                                {#if item.imageUrl}
                                    <img class="thumb" src={item.imageUrl} alt={item.productName} />
                                {/if}
                                <div class="row-id">
                                    <p class="product-name">{item.productName}</p>
                                    <span class="mark">{item.brand || 'Custom product'} · {item.productType || 'Unknown type'}</span>
                                </div>
                                <Chip tone={matchTone(item)}>{matchLabel(item)}</Chip>
                            </div>

                            <div class="row-controls">
                                <Field label="Timing" let:id>
                                    <select {id} value={item.timing} on:change={(e) => updateItem(index, 'timing', e.target.value)}>
                                        <option value="morning">Morning</option>
                                        <option value="night">Night</option>
                                        <option value="both">Both</option>
                                    </select>
                                </Field>
                                <Field label="How often" let:id>
                                    <select {id} value={item.frequency} on:change={(e) => updateItem(index, 'frequency', e.target.value)}>
                                        <option value="daily">Daily</option>
                                        <option value="2-3x weekly">2-3x weekly</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="as needed">As needed</option>
                                    </select>
                                </Field>
                                <Field label="Plan" let:id>
                                    <select {id} value={item.status} on:change={(e) => updateItem(index, 'status', e.target.value)}>
                                        <option value="keep">Keep</option>
                                        <option value="unsure">Unsure</option>
                                        <option value="replace">Want to replace</option>
                                    </select>
                                </Field>
                            </div>

                            <label class="prescribed">
                                <input type="checkbox" checked={!!item.prescribed} on:change={(e) => updateItem(index, 'prescribed', e.target.checked)} />
                                <span>Prescribed to me</span>
                            </label>

                            {#if item.matchConfidence !== 'catalog'}
                                <Field
                                    label="Ingredient list"
                                    hint={ingredientsOf(item)
                                        ? 'Read from the product page. Correct it if it looks wrong.'
                                        : 'We could not find one for this product. Paste it from the box and the rating stops being a guess.'}
                                    let:id
                                    let:describedBy
                                >
                                    <textarea
                                        {id}
                                        aria-describedby={describedBy}
                                        rows="2"
                                        value={ingredientsOf(item)}
                                        on:change={(e) => setIngredients(index, e.target.value)}
                                        placeholder="Water, Niacinamide, Glycerin, ..."
                                    ></textarea>
                                </Field>

                                {#if ingredientsOf(item) && item.ingredientSource}
                                    <p class="provenance">
                                        Ingredients from
                                        <a href={item.ingredientSourceUrl} target="_blank" rel="noopener noreferrer">{item.ingredientSource}</a>,
                                        not from this product's own page.
                                    </p>
                                {/if}

                                {#if !ingredientsOf(item)}
                                    <!-- Read from ingredientHunts directly, not through a
                                         helper: Svelte tracks what the expression names, so
                                         huntFor(item) rendered the first state and then never
                                         updated when the lookup came back. -->
                                    {@const hunt = ingredientHunts[huntKey(item)] || null}
                                    {#if hunt && hunt.loading}
                                        <p class="hunting">Looking for this product's ingredient list elsewhere…</p>
                                    {:else if hunt && hunt.candidates.length > 0}
                                        <p class="hunting">
                                            Found {hunt.candidates.length === 1 ? 'a list' : 'these'} for this product.
                                            Check it looks right before using it.
                                        </p>
                                        <div class="finds">
                                            {#each hunt.candidates as candidate}
                                                <div class="find">
                                                    <p class="find-source">
                                                        <a href={candidate.sourceUrl} target="_blank" rel="noopener noreferrer">{candidate.source}</a>
                                                        <span class="find-kind">{sourceLabel(candidate.sourceKind)}</span>
                                                    </p>
                                                    <p class="find-list">{candidate.ingredients}</p>
                                                    <Button variant="secondary" size="sm" on:click={() => useFoundIngredients(index, candidate)}>
                                                        Use this list
                                                    </Button>
                                                </div>
                                            {/each}
                                        </div>
                                    {:else}
                                        {#if hunt && hunt.message}
                                            <p class="hunting">{hunt.message}</p>
                                        {/if}
                                        {#if !hunt || hunt.configured !== false}
                                            <Button variant="ghost" size="sm" on:click={() => findIngredients(index)}>
                                                {hunt ? 'Look again' : 'Find the ingredients'}
                                            </Button>
                                        {/if}
                                    {/if}
                                {/if}
                            {/if}

                            <Field label="Note" let:id>
                                <input {id} value={noteOf(item)} placeholder="Optional" on:change={(e) => setNote(index, e.target.value)} />
                            </Field>

                            <div class="row-actions">
                                {#if !item.productId}
                                    <Button variant="secondary" size="sm" on:click={() => openResolver(index)}>Find match</Button>
                                {/if}
                                <Button variant="ghost" size="sm" on:click={() => removeItem(index)}>Remove</Button>
                            </div>

                            {#if resolver.index === index}
                                <div class="resolver">
                                    <Field
                                        label="Product name or brand"
                                        hint="Search the catalog, paste a product link, or upload a label photo. Any one of them is enough."
                                        let:id
                                        let:describedBy
                                    >
                                        <input {id} aria-describedby={describedBy} bind:value={resolver.query} placeholder="Search product name, brand, or retailer wording" />
                                    </Field>
                                    <div class="resolver-actions">
                                        <Button variant="secondary" size="sm" loading={resolver.isLoading} busyLabel="Searching" on:click={resolveProduct}>Search catalog</Button>
                                        <Button variant="ghost" size="sm" on:click={closeResolver}>Close</Button>
                                    </div>

                                    <Field label="Product link" let:id>
                                        <input {id} bind:value={resolver.url} placeholder="https://www.amazon.com/… or product page URL" />
                                    </Field>
                                    <div class="resolver-actions">
                                        <Button variant="secondary" size="sm" loading={resolver.isResolvingUrl} busyLabel="Reading link" on:click={resolveProductUrl}>Read product link</Button>
                                    </div>

                                    <Field label="Label photo" let:id>
                                        <label class="upload-inline" for={id}>
                                            <input {id} type="file" accept="image/png,image/jpeg,image/webp" on:change={resolveProductImage} />
                                            {resolver.isResolvingImage ? 'Reading image…' : 'Upload product photo'}
                                        </label>
                                    </Field>

                                    {#if resolver.imagePreview}
                                        <img class="resolver-image-preview" src={resolver.imagePreview} alt="Product label preview" />
                                    {/if}
                                    {#if resolver.message}
                                        <p class="muted">{resolver.message}</p>
                                    {/if}
                                    {#if resolver.matches.length > 0}
                                        <div class="resolver-results">
                                            {#each resolver.matches as match}
                                                <button class="resolver-result" type="button" on:click={() => confirmResolvedProduct(match)}>
                                                    {#if match.imageUrl}
                                                        <img class="thumb" src={match.imageUrl} alt={match.name} />
                                                    {/if}
                                                    <span class="result-name">
                                                        {match.name}
                                                        <span class="mark">{match.brand} · {match.productType || 'product'} · {match.source}</span>
                                                    </span>
                                                    {#if match.confidence}
                                                        <span class="confidence">{match.confidence}%</span>
                                                    {/if}
                                                </button>
                                            {/each}
                                        </div>
                                    {/if}
                                </div>
                            {/if}
                        </div>
                    {/each}
                </div>

                {#if unmatchedItems.length > 0}
                    <div class="caution-note">
                        <Banner tone="caution">
                            {unmatchedItems.length} product{unmatchedItems.length === 1 ? ' still needs' : 's still need'} a catalog match. Use Find match so SkinGenie can confirm the exact product before scoring ingredients.
                        </Banner>
                    </div>
                {/if}

                <div class="footer-actions">
                    <Button
                        variant={analyzeIsPrimary ? 'primary' : 'secondary'}
                        disabled={routineItems.length === 0}
                        loading={isAnalyzing}
                        busyLabel="Rating"
                        on:click={analyzeRoutine}
                    >Rate my routine</Button>
                    <Button
                        variant="secondary"
                        disabled={routineItems.length === 0}
                        loading={isSaving}
                        busyLabel="Saving"
                        on:click={saveRoutine}
                    >{isLoggedIn ? 'Save routine' : 'Save on this device'}</Button>
                </div>
            </Card>
        {/if}
    </div>

    {#if analysis}
        <section class="report">
            <RoutineRating rating={analysis} />

            <div class="footer-actions">
                <Button on:click={goToNextStep}>{onboarding ? 'Choose what’s next' : 'Get products for this'}</Button>
                <Button variant="secondary" on:click={goToScanner}>Check a product against this</Button>
                <Button variant="ghost" on:click={goToProgress}>Track progress</Button>
            </div>
        </section>
    {/if}
</div>
